import SubscriptionAPI from '../api/subscription';

export default (function () {
    var my = {},
        lelements,
        postalCode,
        expressElementAmount;
    /**
     * @type {{ confirmSetup: (args: { elements: any }) => Promise<{ error: {}; setupIntent: { status: "succeeded" | string } }>; elements: any; }}
     */
    var stripe;
    var stripeExpressEvent = new Event('stripe-express-payment-success', { bubbles: true });
    var stripeConfirmEvent = new Event('stripe-express-payment-confirm', { bubbles: true });

    function privateMethod() {

    }

    my.elements = [];

    my.init = function (cardNumberId, cardExpiryId, cardCvcId, zipCodeId, style) {

        function createElements() {
            lelements = stripe.elements();
            /**
             * @type {{
             *      mount: (any) => void;
             *      on: (
             *          type: string,
             *          event: (params: { brand: 'visa' | 'mastercard' | string } & Event) => void
             *      ) => void
             * }}
             */
            let cardNumber = lelements.create('cardNumber', {style: style});
            let cardExpiry = lelements.create('cardExpiry', {style: style});
            let cardCvc = lelements.create('cardCvc', {style: style});
            postalCode = lelements.create('postalCode', {style: style});

            cardNumber.mount(cardNumberId);
            cardExpiry.mount(cardExpiryId);
            cardCvc.mount(cardCvcId);
            postalCode.mount(zipCodeId);

            cardNumber.on('change', function (event) {
                // Switch brand logo
                if (event.brand) {
                    my.setBrandIcon(event.brand);
                }
            });

            my.elements = [cardNumber, cardExpiry, cardCvc, postalCode];
        }

        if (stripe) {
            createElements();

            return Promise.resolve();
        }

        return my.loadStripeScript().then(function () {
            stripe = Stripe(window.Laravel.stripeKey);
            createElements();
        }, function () {
            alert('Failed to Load Stripe Service, reload the page.');
        })
    }

    my.resetAPM = () => {
        expressElementAmount = undefined;
    }

    /**
     * Initialize the Express Checkout Element
     * @param {{mode?: "setup" | "payment" | "subscription"; currency?: string; price?: number; amount?: number }} options
     */
    my.initExpressCheckoutElement = function (options = {}) {
        const APMEnabled = process.env.MIX_APM_ENABLED === 'true';
        this.resetAPM();

        if (!APMEnabled) {
            console.warn('APM is not enabled');
            return Promise.reject('APM is not enabled');
        }
        console.log('Initializing Express Checkout Element', options);

        options = {
            mode: 'setup',
            currency: 'usd',
            appearance: {
                theme: 'dark'
            },
            setupFutureUsage: "off_session",
            ...options
        };

        if (!document.getElementById('express-checkout-element')) {
            console.log('Express Checkout Element not loaded! Retrying initialization...');
            this.initExpressCheckoutElement({ ...options }); // Retry initialization
        }

        console.log('Express Checkout Element Options', options);

        window.expressElement = stripe.elements(options);

        const expressCheckoutElement = expressElement.create('expressCheckout', {
            buttonHeight: 45,
            paymentMethods: {
                'googlePay': 'always',
                'applePay': 'always',
                'amazonPay': 'auto',
                'link': 'never'
            },
            buttonType: {
                'googlePay': 'plain',
                'applePay': 'plain',
            },
            paymentMethodOrder: ['apple_pay', 'google_pay', 'amazon_pay']
        });
        const skeletonLoader = document.getElementById('express-checkout-element-skeleton');

        expressCheckoutElement.mount('#express-checkout-element');

        // misc
        if (skeletonLoader) skeletonLoader.style.display = 'block';
        document.getElementById('option-container').style.display = 'block';

        expressCheckoutElement.on('ready', ({ availablePaymentMethods }) => {
            if (skeletonLoader) skeletonLoader.style.display = 'none';
            
            if (Object.keys(availablePaymentMethods ?? {}).length > 0) {
                document.getElementById('express-checkout-element').style.display = 'block';
                return;
            }

            document.getElementById('option-container').style.display = 'none';
        });

        expressCheckoutElement.on(
            'click',
            /**
             * @param {{ elementType: string; expressPaymentType: 'google_pay' | 'apple_pay' | 'amazon_pay'; resolve: (x: {}) => { } }} event
             */
            (event) => {
                // We need to show some amount instead of $0.00 in only Google Pay & cto show that we need to change the mode too.
                if (event.expressPaymentType === 'google_pay') {
                    this.updateExpressCheckoutElement({ mode: "payment", amount: Math.round(expressElementAmount ?? options.price) })
                }

                event.resolve({
                    emailRequired: true,
                });
            });

        const handleError = (error) => {
            const messageContainer = document.querySelector('#error-message-express-checkout');
            if (messageContainer) {
                messageContainer.textContent = error.message;
            }
        }

        expressCheckoutElement.on('confirm', async (event) => {
            // We need to reset the mode & remove amount to prevent stripe.js exception
            if (event.expressPaymentType === 'google_pay') {
                this.updateExpressCheckoutElement({ mode: "setup", amount: undefined })
            }

            const { error: submitError } = await expressElement.submit();
            if (submitError) {
                handleError(submitError);
                return;
            }

            const { intent: { client_secret: clientSecret } } = await SubscriptionAPI.getAutomaticPaymentIntent()

            const payment = await stripe.confirmSetup({
                elements: expressElement,
                clientSecret,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: "if_required",
            });

            if (payment.error) {
                return handleError(payment.error);
            }

            // Payment successful
            if (payment.setupIntent?.status === 'succeeded') {
                stripeExpressEvent.details = { setupIntent: payment.setupIntent };
                document.dispatchEvent(stripeExpressEvent);
            }
        });

        expressCheckoutElement.on('cancel', (ev) => {
            // Reset the mode & remove amount to prevent stripe.js exception
            this.updateExpressCheckoutElement({ mode: "setup", amount: undefined })
            this.resetAPM();
            console.log('Express Checkout Cancelled', ev);
        });

        return new Promise((resolve) => {
            resolve({ expressCheckoutElement });
        });
    }

    /**
     * Update the Express Checkout Element
     *
     * @param {{ price?: number; amount?: number; mode?: 'setup' | 'payment' | 'subscription' }} updateOptions
     * @return {*}
     */
    my.updateExpressCheckoutElement = function (updateOptions) {
        const APMEnabled = process.env.MIX_APM_ENABLED === 'true';
        if (!APMEnabled) {
            console.error('APM is not enabled');
            return;
        }

        if (window.expressElement) {
            console.log('Updating Express Checkout Element', updateOptions);

            if (updateOptions.price) {
                expressElementAmount = updateOptions.price;
            }

            return window.expressElement.update(updateOptions);
        }

        console.info('Method not implemented: updateExpressCheckoutElement');
    }

    /**
     * Blocks the user from interacting with the payment elements
     */
    my.onPaymentProcessing = function () {
        const expressEle = document.getElementById('express-checkout-element');
        if (expressEle) {
            expressEle.style.pointerEvents = 'none';
            expressEle.style.opacity = '0.7';
        }
    }

    /**
     * Enables the user from interacting with the payment elements
     */
    my.onPaymentProcessed = function () {
        const expressEle = document.getElementById('express-checkout-element');
        if (expressEle) {
            expressEle.style.pointerEvents = 'auto';
            expressEle.style.opacity = '1';
        }
    }

    /*  Update zip code from another element on the page, in our case
         Don't need this if you show the zip code element on the payment page
    */
    my.setZipCode = function (zipcode) {
        postalCode.update({value: zipcode});
    }

    my.processPayment = function (intent, billingDetails, takePayment) {
        console.log('Intent Sent', intent);
        my.setZipCode(billingDetails.address.postal_code);

        if (!takePayment) {
            return new Promise(function (resolve, reject) {
                return resolve({ paymentMethod : null });
            });
        }


        return new Promise(function (resolve, reject) {
            console.log('Elements', my.elements);
            stripe.confirmCardSetup(intent.client_secret, {
                payment_method: {
                    card: my.elements[0],
                    billing_details: billingDetails
                }
            }).then(function (result) {
                if (result.setupIntent) {
                    if (result.setupIntent.status == 'succeeded') {
                        resolve(result.setupIntent);
                    } else {
                        reject('Failed: ' + result.setupIntent.status);
                    }
                } else if (result.error) {
                    reject(result.error);
                } else {
                    reject('Unknown Error Occurred');
                }
            });
        });
    }

    my.loadStripeScript = function () {
        return new Promise(function (resolve, reject) {
            var script = document.createElement('script');
            script.src = 'https://js.stripe.com/v3/';
            script.type = 'text/javascript';
            script.defer = true;
            document.getElementsByTagName('head').item(0).appendChild(script);

            script.onload = function () {
                resolve()
            }
            script.onerror = function () {
                reject()
            }
        })
    }

    my.setBrandIcon = function (brand) {

        var cardBrandToPfClass = {
            'visa': 'pf-visa',
            'mastercard': 'pf-mastercard',
            'amex': 'pf-american-express',
            'discover': 'pf-discover',
            'diners': 'pf-diners',
            'jcb': 'pf-jcb',
            'unknown': 'pf-credit-card',
        }

        var brandIconElement = document.getElementById('brand-icon');
        var pfClass = 'pf-credit-card';
        if (brand in cardBrandToPfClass) {
            pfClass = cardBrandToPfClass[brand];
        }
        for (var i = brandIconElement.classList.length - 1; i >= 0; i--) {
            brandIconElement.classList.remove(brandIconElement.classList[i]);
        }
        brandIconElement.classList.add('pf');
        brandIconElement.classList.add(pfClass);
    }

    return my;
}());
